import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { Box, Tab, Tabs, Typography } from "@mui/material";
import TableCompo from "../Table/TableCompo";
import Vendors from "./Vendors";

const formatInr = (value) =>
  Number(value || 0).toLocaleString("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 2,
  });

const formatWhen = (value) =>
  value
    ? new Date(value).toLocaleString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      })
    : "N.A";

const SplitHistory = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const loadSplits = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/api/v1/vendor/splits`,
      );
      setRows(response.data?.data || []);
    } catch (error) {
      toast.error(
        error?.response?.data?.message || "Could not load payment splits.",
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSplits();
  }, [loadSplits]);

  const tableRows = useMemo(
    () =>
      rows.map((row) => ({
        ...row,
        vendorSummary:
          Array.isArray(row.vendors) && row.vendors.length
            ? row.vendors
                .map(
                  (vendor) =>
                    `${vendor.name || "Vendor"} ${vendor.booking_percentage}% (${formatInr(vendor.amount)})`,
                )
                .join(", ")
            : "N.A",
      })),
    [rows],
  );

  const columns = useMemo(
    () => [
      {
        field: "createdAt",
        headerName: "Date",
        render: (value) => formatWhen(value),
      },
      {
        field: "customer",
        headerName: "Customer",
        render: (value) =>
          value
            ? `${value.firstName || ""} ${value.middleName || ""} ${value.lastName || ""}`.trim() ||
              "N.A"
            : "N.A",
      },
      {
        field: "phone",
        headerName: "Phone",
        render: (_value, row) => row.customer?.phone || "N.A",
      },
      { field: "orderId", headerName: "Order ID" },
      { field: "status", headerName: "Status" },
      {
        field: "vendorSummary",
        headerName: "Vendors",
        minWidth: 320,
        flex: 1.6,
        render: (value) => value || "N.A",
      },
      {
        field: "bookedAmount",
        headerName: "Booked amount",
        render: (value) => formatInr(value),
      },
      {
        field: "jewellerAmount",
        headerName: "Jeweller account",
        render: (value, row) =>
          formatInr(
            value ??
              Number(row.bookedAmount || 0) -
                (row.vendors || []).reduce(
                  (sum, vendor) => sum + Number(vendor.amount || 0),
                  0,
                ),
          ),
      },
      {
        field: "totalAmount",
        headerName: "Live total with GST",
        render: (value) => formatInr(value),
      },
      {
        field: "tax",
        headerName: "Tax",
        render: (value) => formatInr(value),
      },
      {
        field: "marginWithGst",
        headerName: "Margin with GST",
        render: (value) => formatInr(value),
      },
      {
        field: "marginWithoutGst",
        headerName: "Margin without GST",
        render: (value) => formatInr(value),
      },
    ],
    [],
  );

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Each row is one Cashfree payment that was divided across vendors. Pending
        means the customer has not finished payment yet.
      </Typography>
      <TableCompo
        columns={columns}
        data={tableRows}
        loading={loading}
        page={0}
        limit={Math.max(tableRows.length, 10)}
        totalCount={tableRows.length}
        multiline
      />
    </Box>
  );
};

export default function PaymentSplit() {
  const [tab, setTab] = useState(0);

  return (
    <Box>
      <Tabs
        value={tab}
        onChange={(_event, next) => setTab(next)}
        sx={{ mb: 2 }}
      >
        <Tab label="Vendors" sx={{ textTransform: "none", fontWeight: 600 }} />
        <Tab label="Splits" sx={{ textTransform: "none", fontWeight: 600 }} />
      </Tabs>
      {tab === 0 ? <Vendors /> : <SplitHistory />}
    </Box>
  );
}
